export interface UsersLoginRequest {
  readonly email: string;
  readonly password: string;
}

export interface UsersLoginSuccess {
  readonly message: string;
  readonly user: {
    readonly id: string;
    readonly encodedId?: string;
    readonly legalName: string;
    readonly email?: string | null;
    readonly phone?: string | null;
    readonly status: string;
    readonly profileComplete?: boolean;
    readonly profile?: {
      readonly username?: string;
      readonly displayName?: string;
      readonly visibility?: string;
    };
  };
  readonly session: {
    readonly accessToken: string;
    readonly refreshToken: string;
    readonly issuedAt: string;
    readonly expiresInSeconds: number;
    readonly isStub: boolean;
  };
  readonly profileComplete?: boolean;
}

export interface UsersLoginErrorPayload {
  readonly error?: string;
  readonly [key: string]: unknown;
}

export interface UsersLoginOptions {
  readonly endpoint?: string;
  readonly fetcher?: typeof fetch;
  readonly signal?: AbortSignal;
}

export class UsersLoginError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, options: { status: number; payload: unknown }) {
    super(message);
    this.name = "UsersLoginError";
    this.status = options.status;
    this.payload = options.payload;
  }
}

const DEFAULT_USERS_BASE_URL = (
  process.env.NEXT_PUBLIC_USERS_URL ??
  process.env.USERS_URL ??
  `${(process.env.NEXT_PUBLIC_PUBLIC_SCHEME ?? "https").trim()}://${(process.env.NEXT_PUBLIC_PUBLIC_HOST ?? "localhost").trim()}:8083/users`
).trim();

const normalizeBaseUrl = (value: string): string => {
  if (value.endsWith("/")) {
    return value.slice(0, -1);
  }
  return value;
};

const DEFAULT_LOGIN_ENDPOINT = `${normalizeBaseUrl(DEFAULT_USERS_BASE_URL)}/login`;

function getFetcher(fetcher?: typeof fetch): typeof fetch {
  const resolved = fetcher ?? globalThis.fetch;
  if (!resolved) {
    throw new Error("A fetch implementation must be provided to authenticate with the Users service");
  }
  return resolved;
}

export async function authenticateWithPassword(
  credentials: UsersLoginRequest,
  options: UsersLoginOptions = {},
): Promise<UsersLoginSuccess> {
  const fetcher = getFetcher(options.fetcher);
  const endpoint = options.endpoint ?? DEFAULT_LOGIN_ENDPOINT;

  const response = await fetcher(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(credentials),
    cache: "no-store",
    signal: options.signal,
  });

  const payload = (await response.json().catch(() => null)) as UsersLoginErrorPayload | UsersLoginSuccess | null;

  if (!response.ok) {
    const message =
      (payload && typeof (payload as UsersLoginErrorPayload).error === "string"
        ? (payload as UsersLoginErrorPayload).error
        : undefined) ?? response.statusText ?? "Request to users service failed";
    throw new UsersLoginError(message, { status: response.status, payload });
  }

  if (!payload) {
    throw new UsersLoginError("Users service returned an empty response", { status: response.status, payload });
  }

  return payload as UsersLoginSuccess;
}
